/**
 * Where the runway counts each Sage department (mirror of fpa-backend `src/runway/tiles.ts`,
 * ADR-0005): (1) the configured tile listing it; else (2) the configured tile listing its nearest
 * Sage ancestor; else (3) the automatic tile of its top-level Sage parent. Pure, so Settings →
 * Departments can show the effect of an unsaved draft and the mock API can follow the same rules.
 */
import type { DepartmentVia, TileGrouping } from '@/api/types'

export interface HierarchyDept {
  id: string
  parent_id: string | null
}

export interface HierarchyTile {
  code: string
  source_depts: string[]
  hidden?: boolean
}

export interface Placement {
  /** Configured tile code, or the top-level department id for an automatic tile. */
  tile: string
  via: DepartmentVia
  root: string
}

/** An automatic tile: a top-level Sage department and the sub-departments rolled up into it. */
export interface AutoGroup {
  root: string
  /** Root first, then its sub-departments by id. */
  members: string[]
  grouping: Exclude<TileGrouping, 'custom'>
}

export interface HierarchyPlan {
  placement: Map<string, Placement>
  /** Sub-departments each configured tile counts because Sage puts them under a listed one. */
  inherited: Map<string, string[]>
  /** Automatic tiles by root id. */
  groups: AutoGroup[]
}

/** The department and its Sage ancestors, nearest first; stops at cycles and unknown parents. */
export function ancestry(id: string, byId: ReadonlyMap<string, HierarchyDept>): string[] {
  const chain = [id]
  const seen = new Set(chain)
  let parent = byId.get(id)?.parent_id?.trim() || null
  while (parent && !seen.has(parent) && byId.has(parent)) {
    chain.push(parent)
    seen.add(parent)
    parent = byId.get(parent)?.parent_id?.trim() || null
  }
  return chain
}

export function planHierarchy(tiles: readonly HierarchyTile[], departments: readonly HierarchyDept[]): HierarchyPlan {
  const byId = new Map(departments.map((d) => [d.id, d]))
  const listedBy = new Map<string, HierarchyTile>()
  for (const t of tiles) for (const d of t.source_depts) if (!listedBy.has(d)) listedBy.set(d, t)

  const placement = new Map<string, Placement>()
  const inherited = new Map<string, string[]>()
  const members = new Map<string, string[]>()
  const ids = [...new Set([...byId.keys(), ...listedBy.keys()])].sort()
  for (const id of ids) {
    const chain = ancestry(id, byId)
    const root = chain[chain.length - 1] ?? id
    const owner = chain.map((d) => listedBy.get(d)).find((t) => t !== undefined)
    if (owner) {
      const direct = owner.source_depts.includes(id)
      if (!direct) inherited.set(owner.code, [...(inherited.get(owner.code) ?? []), id])
      placement.set(id, { tile: owner.code, via: direct ? 'tile' : 'parent_tile', root })
    } else {
      members.set(root, [...(members.get(root) ?? []), id])
    }
  }

  const groups: AutoGroup[] = [...members.entries()]
    .map(([root, list]) => {
      const ordered = [root, ...list.filter((m) => m !== root).sort()]
      for (const m of ordered) placement.set(m, { tile: root, via: m === root ? 'sage' : 'sage_hierarchy', root })
      return { root, members: ordered, grouping: ordered.length > 1 ? ('sage_hierarchy' as const) : ('sage' as const) }
    })
    .sort((a, b) => a.root.localeCompare(b.root))

  return { placement, inherited, groups }
}
